import axios from 'axios';
import { toast } from '../lib/toast';

// Base URL from environment — must be set at build time
const baseURL = process.env.REACT_APP_BACKEND_URL;
if (!baseURL) {
  throw new Error(
    'REACT_APP_BACKEND_URL is not set. Define it in .env (dev) or as a build arg (prod).'
  );
}

const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    if (
      typeof config.url === 'string' &&
      config.url.startsWith('/api/') &&
      !config.url.startsWith('/api/v1/')
    ) {
      config.url = config.url.replace('/api/', '/api/v1/');
    }
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      localStorage.getItem('refreshToken')
    ) {
      originalRequest._retry = true;
      try {
        const refreshRes = await axios.post(
          `${baseURL}/api/v1/auth/jwt/refresh/`,
          {
            refresh: localStorage.getItem('refreshToken'),
          }
        );

        const newAccessToken = refreshRes.data.access;
        localStorage.setItem('accessToken', newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshErr) {
        console.error('Refresh token failed:', refreshErr);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        toast.error('Your session expired. Please log in again.');
        window.location.href = '/login';
      }
    }

    if (!error.response) {
      console.error('Network error — server may be down');
      if (!originalRequest?._skipGlobalErrorToast) {
        toast.error('Network error. Please check your connection and try again.');
      }
    } else if (error.response.status >= 500) {
      console.error('Server error:', error.response.status);
      if (!originalRequest?._skipGlobalErrorToast) {
        toast.error('Server error. Please try again in a moment.');
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
